import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle, Spinner } from "@phosphor-icons/react";
import { api, BACKEND } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [pkg, setPkg] = useState(null);
  const [files, setFiles] = useState(null);
  const { refreshUser } = useAuth();
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          setPkg(data.package_id);
          refreshUser();
          return;
        }
      } catch (e) {
        // keep polling
      }
      attempts.current += 1;
      if (attempts.current < 15) {
        setTimeout(poll, 2500);
      } else {
        setStatus("timeout");
      }
    };
    poll();
  }, [sessionId]);

  useEffect(() => {
    if (status === "paid" && pkg === "document_pack" && sessionId) {
      api.get(`/products/document-pack/sign/${sessionId}`).then((r) => {
        setFiles(r.data.files);
        const first = r.data.files[0];
        if (first) {
          const a = document.createElement("a");
          a.href = `${BACKEND}${first.url}`;
          a.download = first.name;
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
      }).catch(() => {});
    }
  }, [status, pkg, sessionId]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#141413] px-6 text-[#F4F4F0]" data-testid="payment-success-page">
      <div className="w-full max-w-lg border border-[#2E2E2A] bg-[#1E1E1C] p-12 text-center">
        {status === "paid" ? (
          <>
            <CheckCircle size={56} weight="duotone" className="mx-auto text-[#C49A6C]" />
            <h1 data-testid="payment-success-heading" className="mt-6 font-heading text-4xl font-light">You're all set.</h1>
            <p className="mt-4 text-sm leading-relaxed text-[#A3A39C]">
              {pkg === "document_pack"
                ? "Payment confirmed — your Document Pack download should start automatically. If it doesn't, use the buttons below."
                : "Your purchase is confirmed. Head to your dashboard for your modules, or book your session now."}
            </p>
            {files && (
              <div className="mt-8 space-y-3" data-testid="document-pack-downloads">
                {files.map((f, i) => (
                  <a
                    key={i}
                    href={`${BACKEND}${f.url}`}
                    download={f.name}
                    data-testid={`download-file-${i}`}
                    className="flex items-center justify-center gap-2 border border-[#C49A6C] px-6 py-3 text-xs uppercase tracking-[0.15em] text-[#C49A6C] transition-colors duration-200 hover:bg-[#C49A6C] hover:text-[#141413]"
                  >
                    {f.name}
                  </a>
                ))}
              </div>
            )}
            <Link
              to="/dashboard"
              data-testid="go-to-dashboard-button"
              className="mt-8 inline-block border border-[#C49A6C] bg-[#C49A6C] px-8 py-4 text-xs uppercase tracking-[0.15em] text-[#141413] transition-colors duration-200 hover:bg-transparent hover:text-[#C49A6C]"
            >
              Go to Dashboard
            </Link>
          </>
        ) : status === "timeout" || status === "error" ? (
          <>
            <h1 data-testid="payment-pending-heading" className="font-heading text-3xl font-light">Almost there...</h1>
            <p className="mt-4 text-sm text-[#A3A39C]">
              We're still confirming your payment. It usually takes a few seconds — check your dashboard shortly.
            </p>
            <Link to="/dashboard" data-testid="go-to-dashboard-button" className="mt-8 inline-block border border-[#C49A6C] px-8 py-4 text-xs uppercase tracking-[0.15em] text-[#C49A6C]">
              Go to Dashboard
            </Link>
          </>
        ) : (
          <>
            <Spinner size={48} className="mx-auto animate-spin text-[#C49A6C]" />
            <h1 className="mt-6 font-heading text-3xl font-light" data-testid="payment-confirming-heading">Confirming your payment...</h1>
            <p className="mt-4 text-sm text-[#A3A39C]">This takes a few seconds. Don't close this page.</p>
          </>
        )}
      </div>
    </div>
  );
}
